import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { getApiErrorMessage } from '@/lib/api/error';
import {
  UserNotificationPreferencesService,
  userNotificationPreferencesQueryKey,
} from '@/services/user-notification-preferences.service';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BellRing, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000;

function getDismissKey(userId: string) {
  return `vira-tempo:push-prompt-dismissed:${userId}`;
}

function wasRecentlyDismissed(userId: string) {
  const dismissedAt = Number(window.localStorage.getItem(getDismissKey(userId)));

  return Number.isFinite(dismissedAt) && Date.now() - dismissedAt < DISMISS_DURATION_MS;
}

export function PushPermissionPrompt() {
  const queryClient = useQueryClient();
  const { isAuthenticated, user } = useAuth();
  const { state, enablePush } = usePushNotifications(isAuthenticated);
  const preferencesQuery = useQuery({
    queryKey: userNotificationPreferencesQueryKey,
    queryFn: UserNotificationPreferencesService.get,
    enabled: isAuthenticated,
  });
  const [dismissedUserId, setDismissedUserId] = useState<string | null>(null);
  const dismissed = Boolean(user?.id && (dismissedUserId === user.id || wasRecentlyDismissed(user.id)));

  if (
    !isAuthenticated ||
    !user ||
    state !== 'idle' ||
    dismissed ||
    preferencesQuery.isLoading ||
    preferencesQuery.data?.pushEnabled === false
  ) {
    return null;
  }

  const dismiss = () => {
    window.localStorage.setItem(getDismissKey(user.id), String(Date.now()));
    setDismissedUserId(user.id);
  };

  const activate = async () => {
    try {
      const activated = await enablePush();

      if (activated) {
        const preferences = await UserNotificationPreferencesService.update({ pushEnabled: true });
        queryClient.setQueryData(userNotificationPreferencesQueryKey, preferences);
        toast.success('Notificações ativadas', {
          description: 'Você receberá avisos importantes mesmo fora do app.',
        });
      }
    } catch (error) {
      toast.error('Não foi possível ativar as notificações', {
        description: getApiErrorMessage(error, 'Tente novamente nas configurações de notificações.'),
      });
    }
  };

  return (
    <aside className="fixed right-4 bottom-4 left-4 z-50 mx-auto max-w-md rounded-2xl border bg-white p-4 shadow-xl sm:right-6 sm:bottom-6 sm:left-auto sm:p-5">
      <button
        type="button"
        onClick={dismiss}
        className="text-muted-foreground hover:text-foreground absolute top-3 right-3 rounded-lg p-1 transition-colors"
        aria-label="Fechar convite de notificações"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="flex gap-3 pr-5">
        <span className="bg-primary/10 text-primary flex h-10 w-10 shrink-0 items-center justify-center rounded-xl">
          <BellRing className="h-5 w-5" aria-hidden="true" />
        </span>
        <div>
          <h2 className="text-sm font-semibold">Não perca seu horário</h2>
          <p className="text-muted-foreground mt-1 text-sm leading-relaxed">
            Ative as notificações para receber confirmações e avisos da fila em tempo real.
          </p>
        </div>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={dismiss}>
          Agora não
        </Button>
        <Button type="button" size="sm" onClick={() => void activate()}>
          Ativar notificações
        </Button>
      </div>
    </aside>
  );
}
